// Used to auto fill up schedule by parameters

import { useState } from 'react';
import { toast } from 'react-toastify';
import moment from 'moment';
import MultiSelect from './MultiSelect';
import Input from './Input';
import InputDate from './InputDate';
import Select from './Select';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import {
  defaultDuration,
  scheduleStartHour,
  scheduleEndHour,
  scheduleStepMin,
} from '../utils/consts';
import {
  parseTimeToMinutes,
  time12To24H,
  getTeamDivision,
  hourTo12H,
  calcBracketDepth,
  toOptions,
} from '../utils/misc';
import {
  GameTypes,
  PlayoffRounds,
  PlayoffRoundOrder,
  roundIndex,
} from '../utils/enums';
import { useDivisions } from '../utils/hooks';
import { useFirebase } from './FirebaseContextProvider';
import { tournamentGamesCreate } from '../utils/firebase';
import { useModal } from './ModalProvider';
import Spinner from './Spinner';
import Fieldset from './Fieldset';
import RadioButton from './RadioButton';
import './ScheduleGenerator.scss';

const playoffRoundOptions = Object.keys(PlayoffRounds)
  .reverse()
  .map((label) => {
    const count = roundIndex('F') - roundIndex(label) + 1;
    return count === 1 ? `1 round: ${label}` : `${count} rounds: from ${label}`;
  });


// Returns the starting round string (from PlayoffRounds) for the selected option
const getStartRoundFromLabel = (label) => {
  const match = /(?:from )?([A-Z0-9]+)$/.exec(String(label || ''));
  return match && PlayoffRounds[match[1]] ? match[1] : null;
};

// --- Playoff bracket helpers ---
const isPowerOfTwo = (n) => n > 0 && (n & (n - 1)) === 0;

// Standard bracket seed order (e.g. 8 -> [1,8,4,5,2,7,3,6], 16 -> [1,16,8,9,...])
const buildSeedOrder = (size) => {
  if (size <= 2) return size === 2 ? [1, 2] : [1];

  let order = [1, 2];
  let cur = 2;

  while (cur < size) {
    const nextSize = cur * 2;
    const next = [];
    for (const s of order) {
      next.push(s);
      next.push(nextSize + 1 - s);
    }
    order = next;
    cur = nextSize;
  }
  return order;
};

// Mutates prelim-round games in-place: renumbers their placement so it matches
// bracket order (top-to-bottom), i.e. placement = 2 * nextGamePlacement.
// nextGamePlacement is computed locally and never stored on the game object.
const renumberPrelimPlacements = (playoffGames) => {
  if (playoffGames.length === 0) return;

  const byRound = new Map();
  for (const g of playoffGames) {
    if (!byRound.has(g.round)) byRound.set(g.round, []);
    byRound.get(g.round).push(g);
  }

  const rounds = Array.from(byRound.keys()).sort(
    (a, b) => roundIndex(a) - roundIndex(b),
  );

  for (const [ri, r] of rounds.entries()) {
    const cur = (byRound.get(r) || [])
      .slice()
      .sort((a, b) => a.placement - b.placement);
    const nextRound = rounds[ri + 1];
    const next = nextRound ? byRound.get(nextRound) : undefined;
    if (!next || !next.length) continue;

    const nextGamesCount = next.length;
    const expectedCurIfNormal = 2 * nextGamesCount;
    const isPrelim = cur.length < expectedCurIfNormal;

    // Normal round: no renumbering needed
    if (!isPrelim) continue;

    // Prelim/play-in round: map each prelim winner to a specific seed-slot in next round using bracket order
    // byesCount = slotsInNextRound - gamesInPrelimRound
    const r2Size = 2 * nextGamesCount; // number of seed-slots in next round
    const byesCount = r2Size - cur.length;

    // Compute nextGamePlacement locally (not stored on game objects)
    const nextPlacementMap = new Map();

    if (!isPowerOfTwo(r2Size)) {
      for (const g of cur) nextPlacementMap.set(g, 1);
    } else {
      const seedOrder = buildSeedOrder(r2Size);

      for (const g of cur) {
        // This prelim game's winner fills seed-slot: byesCount + placement (matches backend seeding order)
        const seedSlot = byesCount + g.placement;
        const idx = seedOrder.indexOf(seedSlot);
        nextPlacementMap.set(g, idx >= 0 ? Math.floor(idx / 2) + 1 : 1);
      }
    }

    // Renumber: placement = 2 * nextGamePlacement (even numbers, matching positional-math in Brackets)
    cur.sort((a, b) => nextPlacementMap.get(a) - nextPlacementMap.get(b));
    cur.forEach((g) => {
      g.placement = 2 * nextPlacementMap.get(g);
    });
  }
};

export default function ScheduleGenerator({ id, date }) {
  const [spinner, setSpinner] = useState(false);
  const { tournamentsById, teamsById, coachesByTeamId } = useFirebase();
  const getCoachIds = (teamId) =>
    (coachesByTeamId.get(teamId) || []).map((c) => c.id);
  const { closeModal } = useModal();
  const tournament = tournamentsById.get(id);
  const { fields, games } = tournament;
  const divisions = useDivisions(id);

  const haveTeamOrCoachConflict = (g1, g2) => {
    if (
      [g2.team1, g2.team2].includes(g1.team1) ||
      [g2.team1, g2.team2].includes(g1.team2)
    )
      return true;
    const g1Coaches = [...getCoachIds(g1.team1), ...getCoachIds(g1.team2)];
    const g2Coaches = [...getCoachIds(g2.team1), ...getCoachIds(g2.team2)];
    return g1Coaches.some((c) => g2Coaches.includes(c));
  };

  const initialValues = {
    divisions: [],
    poolMode: 'perTeam',
    poolgames: '',
    poolgamesPerTeam: '',
    playoffMode: 'rounds',
    playoffRounds: '',
    playoffs: '',
    fields: [],
    date: moment(date).toDate(),
    start: hourTo12H(scheduleStartHour),
    duration: defaultDuration,
  };

  const validationSchema = new Yup.ObjectSchema({
    divisions: Yup.array()
      .of(Yup.string())
      .min(1, 'Required')
      .required('Required'),
    poolMode: Yup.string().oneOf(['total', 'perTeam']).required('Required'),
    poolgames: Yup.number().when('poolMode', {
      is: 'total',
      then: (schema) =>
        schema
          .typeError('Invalid')
          .integer('Invalid')
          .min(0, 'Invalid')
          .required('Invalid'),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
    poolgamesPerTeam: Yup.number().when('poolMode', {
      is: 'perTeam',
      then: (schema) =>
        schema
          .typeError('Invalid')
          .integer('Invalid')
          .min(0, 'Invalid')
          .required('Invalid'),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
    playoffMode: Yup.string().oneOf(['rounds', 'total']).required('Required'),
    playoffRounds: Yup.string().when('playoffMode', {
      is: 'rounds',
      then: (schema) =>
        schema.oneOf([...playoffRoundOptions], 'Invalid').required('Required'),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
    playoffs: Yup.number().when('playoffMode', {
      is: 'total',
      then: (schema) =>
        schema
          .typeError('Invalid')
          .integer('Invalid')
          .min(0, 'Invalid')
          .required('Invalid'),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
    fields: Yup.array().min(1, 'Required'),
    start: Yup.string()
      .matches(/^ *0?(1[0-2]|[1-9]) *: *[0-5][0-9] *(am|pm) *$/i, 'Invalid')
      .test('within-schedule-range', 'Out of range', function (value) {
        if (!value) return true;
        if (!/^ *0?(1[0-2]|[1-9]) *: *[0-5][0-9] *(am|pm) *$/i.test(value))
          return true;

        const valueMinutes = parseTimeToMinutes(value);
        const startMinutes = scheduleStartHour * 60;
        const endMinutes = scheduleEndHour * 60 - scheduleStepMin;

        return valueMinutes >= startMinutes && valueMinutes <= endMinutes;
      })
      .required('Required'),
    duration: Yup.number()
      .typeError('Invalid')
      .integer('Invalid')
      .min(0, 'Invalid'),
    date: Yup.date().required('Required'),
  });

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      setSpinner(true);

      try {
        const {
          divisions: selectedDivisions,
          poolMode,
          poolgames,
          poolgamesPerTeam,
          playoffMode,
          playoffRounds,
          playoffs,
          fields: selFieldIds,
          date: dateObj,
          start,
          duration,
        } = values;

        const poolgamesTotalNum = +poolgames || 0;
        const poolgamesPerTeamNum = +poolgamesPerTeam || 0;

        // How many teams we want in the playoff bracket (not games)
        let requestedPlayoffTeams = 0;
        if (playoffMode === 'total') {
          const requestedGames = +playoffs || 0;
          requestedPlayoffTeams = Math.max(0, requestedGames + 1);
        } else if (playoffMode === 'rounds') {
          const startRound = getStartRoundFromLabel(playoffRounds);
          if (!startRound) throw new Error('Invalid playoff rounds selection');
          const numRounds = roundIndex('F') - roundIndex(startRound) + 1;
          requestedPlayoffTeams = Math.pow(2, numRounds);
        }

        // Check for existing playoff games in selected divisions
        if (requestedPlayoffTeams >= 2) {
          const divisionsWithPlayoffs = selectedDivisions.filter((div) =>
            games.some(
              (g) => g.type === GameTypes.PLAYOFF && g.division === div,
            ),
          );
          if (divisionsWithPlayoffs.length > 0) {
            throw new Error(
              `Playoff games already exist for: ${divisionsWithPlayoffs.join(', ')}. Please clear existing playoff games first.`,
            );
          }
        }

        const dateStr = moment(dateObj).format('YYYY-MM-DD');
        const durationMin = parseInt(duration, 10);
        const initialFromTime = time12To24H(start);

        // --- common data for all divisions ---
        const selFieldIdSet = new Set(selFieldIds);
        const selectedFields = fields.filter((f) => selFieldIdSet.has(f.id));
        const existingGamesForDate = games.filter((g) => g.date === dateStr);
        const scheduled = [...existingGamesForDate];
        const initialCount = scheduled.length;

        function getDivisionFromGame(game) {
          if (game.division) return game.division;
          if (!game.team1) return null;
          const team = teamsById.get(game.team1);
          if (!team) return null;
          return getTeamDivision(team);
        }

        // --- helper: check conflict for pool scheduling ---
        function hasConflictAt(startTime, g) {
          const aEnd = startTime.clone().add(durationMin, 'minutes');
          for (const sg of scheduled) {
            if (sg.type !== GameTypes.POOLGAME) continue;
            const bStart = moment(`${dateStr} ${sg.start}`, 'YYYY-MM-DD HH:mm');
            const bEnd = bStart.clone().add(sg.duration, 'minutes');
            const overlap = startTime.isBefore(bEnd) && aEnd.isAfter(bStart);
            if (!overlap) continue;

            if (haveTeamOrCoachConflict(g, sg)) return true;
          }
          return false;
        }

        // --- helper: is field free at given time ---
        function isFieldFree(fieldId, startTime) {
          const end = startTime.clone().add(durationMin, 'minutes');
          for (const sg of scheduled) {
            if (sg.field !== fieldId) continue;
            const sgStart = moment(
              `${dateStr} ${sg.start}`,
              'YYYY-MM-DD HH:mm',
            );
            const sgEnd = sgStart.clone().add(sg.duration, 'minutes');
            if (startTime.isBefore(sgEnd) && end.isAfter(sgStart)) return false;
          }
          return true;
        }

        // --- helper: any conflict if g goes to startTime/fieldId ---
        function hasAnyConflict({ startTime, fieldId, g }) {
          const end = startTime.clone().add(durationMin, 'minutes');
          for (const sg of scheduled) {
            if (sg === g) continue;
            const sgS = moment(`${dateStr} ${sg.start}`, 'YYYY-MM-DD HH:mm');
            const sgE = sgS.clone().add(sg.duration, 'minutes');
            const overlap = startTime.isBefore(sgE) && end.isAfter(sgS);
            if (!overlap) continue;

            if (sg.field === fieldId) return true;
            if (haveTeamOrCoachConflict(g, sg)) return true;
          }
          return false;
        }

        // --- process each selected division independently but on shared schedule ---
        for (const division of selectedDivisions) {
          const isGameOfDivision = (game) =>
            getDivisionFromGame(game) === division;

          function getDivisionGamesCountOnField(fieldId) {
            let count = 0;
            for (const sg of scheduled) {
              if (sg.field !== fieldId) continue;
              if (!isGameOfDivision(sg)) continue;
              count++;
            }
            return count;
          }

          const divisionTeams = tournament.teams
            .filter((t) => t.admitted)
            .map((ref) => teamsById.get(ref.id))
            .filter(Boolean)
            .filter((team) => getTeamDivision(team) === division);

          if (!divisionTeams.length) continue;

          const teamCountInDivision = divisionTeams.length;

          let divisionPoolCount = poolgamesTotalNum;
          if (poolMode === 'perTeam')
            divisionPoolCount = Math.ceil(
              (teamCountInDivision * poolgamesPerTeamNum) / 2,
            );

          // --- build pool games with equal-appearance distribution ---
          const totalAppearances = divisionPoolCount * 2;
          const n = divisionTeams.length;
          const base = Math.floor(totalAppearances / n);
          const extra = totalAppearances % n;

          const appsLeft = {};
          divisionTeams.forEach((t, i) => {
            appsLeft[t.id] = base + (i < extra ? 1 : 0);
          });

          const poolGames = [];
          const playedPairs = new Set();
          while (poolGames.length < divisionPoolCount) {
            const slots = Object.entries(appsLeft)
              .filter(([, c]) => c > 0)
              .sort((a, b) => b[1] - a[1]);
            if (slots.length < 2) break;

            let pair = null;
            for (let i = 0; i < slots.length; i++) {
              for (let j = i + 1; j < slots.length; j++) {
                const [id1] = slots[i];
                const [id2] = slots[j];
                if (
                  !playedPairs.has(`${id1}-${id2}`) &&
                  !playedPairs.has(`${id2}-${id1}`)
                ) {
                  pair = [id1, id2];
                  break;
                }
              }
              if (pair) break;
            }
            if (pair === null) pair = [slots[0][0], slots[1][0]];

            const [t1, t2] = pair;
            playedPairs.add(`${t1}-${t2}`);
            appsLeft[t1]--;
            appsLeft[t2]--;
            poolGames.push({
              team1: t1,
              team2: t2,
              round: null,
              placement: null,
              type: GameTypes.POOLGAME,
            });
          }

          // --- build a single-elim playoff bracket with byes, with max-bye rule ---
          const eligibleTeams = Math.min(
            requestedPlayoffTeams || 0,
            teamCountInDivision,
          );

          const { numRounds, gamesEachRound } = calcBracketDepth(eligibleTeams);

          const playoffGames = [];
          if (numRounds > 0) {
            let rndIdx = roundIndex('F') - numRounds + 1;
            for (const count of gamesEachRound) {
              for (let i = 1; i <= count; i++) {
                playoffGames.push({
                  team1: null,
                  team2: null,
                  round: PlayoffRoundOrder[rndIdx],
                  placement: i,
                  type: GameTypes.PLAYOFF,
                });
              }
              rndIdx++;
            }
            renumberPrelimPlacements(playoffGames);
          }

          // --- helper: rebalance one freed time slot for better field rank within division ---
          const rebalanceSameTimeSlot = (freedFieldId, freedTimeStr) => {
            const poolSched = scheduled.filter(
              (g) => g.type === GameTypes.POOLGAME && isGameOfDivision(g),
            );
            if (!poolSched.length) return;

            const freedBefore = getDivisionGamesCountOnField(freedFieldId);

            for (const sg of poolSched) {
              if (sg.field === freedFieldId) continue;
              if (sg.start !== freedTimeStr) continue;

              const currentBefore = getDivisionGamesCountOnField(sg.field);
              const freedAfter = freedBefore + 1;
              const currentAfter = currentBefore - 1;

              if (freedAfter > currentAfter) {
                sg.field = freedFieldId;
                break;
              }
            }
          };

          // --- schedule pool games one by one using field priority ---
          const poolRemaining = [...poolGames];
          const startOfDay = moment(
            `${dateStr} ${initialFromTime}`,
            'YYYY-MM-DD HH:mm',
          );
          const endOfDay = moment(
            `${dateStr} ${String(scheduleEndHour).padStart(2, '0')}:00`,
            'YYYY-MM-DD HH:mm',
          );

          while (poolRemaining.length) {
            const g = poolRemaining.shift();
            let placed = false;
            let cursor = startOfDay.clone();

            while (
              cursor
                .clone()
                .add(durationMin, 'minutes')
                .isSameOrBefore(endOfDay)
            ) {
              const freeRankedFields = selectedFields
                .filter((f) => isFieldFree(f.id, cursor))
                .sort(
                  (a, b) =>
                    getDivisionGamesCountOnField(b.id) -
                    getDivisionGamesCountOnField(a.id),
                );

              if (!freeRankedFields.length) {
                cursor.add(scheduleStepMin, 'minutes');
                continue;
              }

              let placedHere = false;
              for (const fieldInfo of freeRankedFields) {
                if (hasConflictAt(cursor.clone(), g)) continue;
                scheduled.push({
                  ...g,
                  date: dateStr,
                  start: cursor.format('HH:mm'),
                  duration: durationMin,
                  field: fieldInfo.id,
                  division,
                });
                placed = true;
                placedHere = true;
                break;
              }

              if (placedHere) break;

              cursor.add(scheduleStepMin, 'minutes');
            }

            if (!placed) {
              throw new Error(
                'Unable to schedule all pool games within the day',
              );
            }
          }

          // --- secondary densification: bubble gaps forward by two-move swaps ---
          const densifyPoolGames = () => {
            const poolSched = scheduled.filter(
              (g) => g.type === GameTypes.POOLGAME && isGameOfDivision(g),
            );
            if (!poolSched.length) return false;

            const lastPoolStart = poolSched.reduce(
              (max, g) => {
                const st = moment(`${dateStr} ${g.start}`, 'YYYY-MM-DD HH:mm');
                return st.isAfter(max) ? st : max;
              },
              moment(`${dateStr} ${initialFromTime}`, 'YYYY-MM-DD HH:mm'),
            );

            const gamesByField = {};
            for (const f of selectedFields) {
              gamesByField[f.id] = scheduled
                .filter((g) => g.field === f.id)
                .sort((a, b) =>
                  moment(`${dateStr} ${a.start}`, 'YYYY-MM-DD HH:mm').diff(
                    moment(`${dateStr} ${b.start}`, 'YYYY-MM-DD HH:mm'),
                  ),
                );
            }

            for (const fieldInfo of selectedFields) {
              const gamesOnField = gamesByField[fieldInfo.id];
              let windowStart = moment(
                `${dateStr} ${initialFromTime}`,
                'YYYY-MM-DD HH:mm',
              );

              for (let i = 0; i <= gamesOnField.length; i++) {
                const nextStart =
                  i < gamesOnField.length
                    ? moment(
                        `${dateStr} ${gamesOnField[i].start}`,
                        'YYYY-MM-DD HH:mm',
                      )
                    : lastPoolStart.clone();

                const gap = nextStart.diff(windowStart, 'minutes');
                if (gap >= durationMin) {
                  for (const g1 of poolSched) {
                    const g1Start = moment(
                      `${dateStr} ${g1.start}`,
                      'YYYY-MM-DD HH:mm',
                    );
                    if (!g1Start.isBefore(windowStart)) continue;

                    const conflict1 = hasAnyConflict({
                      startTime: windowStart,
                      fieldId: fieldInfo.id,
                      g: g1,
                    });
                    if (conflict1) continue;

                    const oldField = g1.field;
                    const oldStart = g1Start.clone();

                    for (const g2 of poolSched) {
                      if (!isGameOfDivision(g2)) continue;
                      if (g2 === g1) continue;

                      const g2Orig = moment(
                        `${dateStr} ${g2.start}`,
                        'YYYY-MM-DD HH:mm',
                      );
                      if (g2Orig.isBefore(nextStart)) continue;

                      const g2End = oldStart
                        .clone()
                        .add(durationMin, 'minutes');

                      let conflictField = false;
                      for (const sg of scheduled) {
                        if (sg === g1 || sg === g2) continue;
                        if (sg.field !== oldField) continue;
                        const sgS = moment(
                          `${dateStr} ${sg.start}`,
                          'YYYY-MM-DD HH:mm',
                        );
                        const sgE = sgS.clone().add(sg.duration, 'minutes');
                        if (oldStart.isBefore(sgE) && g2End.isAfter(sgS)) {
                          conflictField = true;
                          break;
                        }
                      }
                      if (conflictField) continue;

                      let conflict2 = false;
                      for (const sg of scheduled) {
                        if (sg === g1 || sg === g2) continue;
                        const sgS = moment(
                          `${dateStr} ${sg.start}`,
                          'YYYY-MM-DD HH:mm',
                        );
                        const sgE = sgS.clone().add(sg.duration, 'minutes');
                        const overlap =
                          oldStart.isBefore(sgE) && g2End.isAfter(sgS);
                        if (!overlap) continue;

                        if (haveTeamOrCoachConflict(g2, sg)) {
                          conflict2 = true;
                          break;
                        }
                      }
                      if (conflict2) continue;

                      const freedFieldId = g2.field;
                      const freedTimeStr = g2.start;

                      g1.field = fieldInfo.id;
                      g1.start = windowStart.format('HH:mm');
                      g2.field = oldField;
                      g2.start = oldStart.format('HH:mm');

                      rebalanceSameTimeSlot(freedFieldId, freedTimeStr);

                      return true;
                    }
                  }
                }

                windowStart = nextStart.clone().add(durationMin, 'minutes');
              }
            }
            return false;
          };

          while (densifyPoolGames()) {}

          // find when pool finished
          const poolEnd = scheduled
            .filter((g) => g.type === GameTypes.POOLGAME && isGameOfDivision(g))
            .reduce(
              (max, sg) => {
                const end = moment(
                  `${dateStr} ${sg.start}`,
                  'YYYY-MM-DD HH:mm',
                ).add(sg.duration, 'minutes');
                return end.isAfter(max) ? end : max;
              },
              moment(`${dateStr} ${initialFromTime}`, 'YYYY-MM-DD HH:mm'),
            );

          // schedule playoffs round by round using field priority
          if (playoffGames.length) {
            let roundStart = poolEnd;
            const uniqueRounds = [
              ...new Set(playoffGames.map((g) => g.round)),
            ].sort((a, b) => roundIndex(a) - roundIndex(b));

            for (const r of uniqueRounds) {
              const roundGames = playoffGames.filter((g) => g.round === r);

              for (const g of roundGames) {
                let placed = false;
                let cursor = roundStart.clone();

                while (
                  cursor
                    .clone()
                    .add(durationMin, 'minutes')
                    .isSameOrBefore(endOfDay)
                ) {
                  const freeRankedFields = selectedFields
                    .filter((f) => isFieldFree(f.id, cursor))
                    .sort(
                      (a, b) =>
                        getDivisionGamesCountOnField(b.id) -
                        getDivisionGamesCountOnField(a.id),
                    );

                  if (!freeRankedFields.length) {
                    cursor.add(scheduleStepMin, 'minutes');
                    continue;
                  }

                  const fieldInfo = freeRankedFields[0]; // take field with highest division density

                  scheduled.push({
                    ...g,
                    date: dateStr,
                    start: cursor.format('HH:mm'),
                    duration: durationMin,
                    field: fieldInfo.id,
                    division,
                  });

                  placed = true;
                  break;
                }

                if (!placed) {
                  throw new Error(`Unable to schedule playoff round ${r}`);
                }
              }

              roundStart = scheduled
                .filter(
                  (g) =>
                    g.type === GameTypes.PLAYOFF &&
                    g.round === r &&
                    g.division === division,
                )
                .reduce((max, sg) => {
                  const end = moment(
                    `${dateStr} ${sg.start}`,
                    'YYYY-MM-DD HH:mm',
                  ).add(sg.duration, 'minutes');
                  return end.isAfter(max) ? end : max;
                }, roundStart);
            }
          }
        }

        // --- write them all to Firebase ---
        const newGames = scheduled.slice(initialCount);
        await tournamentGamesCreate({
          id,
          games: newGames.map((g) => ({ ...g, referees: [] })),
        });

        closeModal();
        formik.resetForm();
      } catch (e) {
        toast.error(e.message);
      } finally {
        setSpinner(false);
      }
    },
  });

  return (
    <form className='schedule-generator-form' onSubmit={formik.handleSubmit}>
      <div className='division-fields-wrap'>
        <MultiSelect
          options={divisions.map((d) => ({ value: d, label: d }))}
          value={formik.values.divisions}
          onChange={(v) => formik.setFieldValue('divisions', v)}
          placeholder='Divisions'
          error={formik.touched.divisions && formik.errors.divisions}
          sortMode
        />
        <MultiSelect
          options={toOptions(fields, { sort: false })}
          value={formik.values.fields}
          onChange={(v) => formik.setFieldValue('fields', v)}
          placeholder='Fields'
          error={formik.touched.fields && formik.errors.fields}
        />
      </div>
      <div className='date-start-duration'>
        <InputDate
          name='date'
          value={formik.values.date}
          onChange={(d) => formik.setFieldValue('date', d)}
          minDate={moment(tournament.startDate).toDate()}
          maxDate={moment(tournament.endDate).toDate()}
          placeholder='Date'
          error={formik.touched.date && formik.errors.date}
        />
        <div className='start-duration'>
          <Input
            className='time'
            name='start'
            placeholder='Time'
            {...formik.getFieldProps('start')}
            error={formik.touched.start && formik.errors.start}
          />
          <div className='duration-wrap'>
            <Input
              className='duration'
              name='duration'
              placeholder='Duration'
              {...formik.getFieldProps('duration')}
              error={formik.touched.duration && formik.errors.duration}
            />
            <p className='min'>min</p>
          </div>
        </div>
      </div>
      <div className='poolgames-playoffs-wrap'>
        <Fieldset label='Pool games' className='poolgames-fieldset'>
          <div className='poolgames-mode'>
            <div className='poolgames-row'>
              <RadioButton
                label=''
                name='poolMode'
                value='perTeam'
                selectedValue={formik.values.poolMode}
                onChange={(val) => formik.setFieldValue('poolMode', val)}
              />
              <Input
                className='poolgames-input'
                name='poolgamesPerTeam'
                placeholder='Per team'
                disabled={formik.values.poolMode !== 'perTeam'}
                {...formik.getFieldProps('poolgamesPerTeam')}
                error={
                  formik.touched.poolgamesPerTeam &&
                  formik.errors.poolgamesPerTeam
                }
              />
            </div>
            <div className='poolgames-row'>
              <RadioButton
                label=''
                name='poolMode'
                value='total'
                selectedValue={formik.values.poolMode}
                onChange={(val) => formik.setFieldValue('poolMode', val)}
              />
              <Input
                className='poolgames-input'
                name='poolgames'
                placeholder='In total'
                disabled={formik.values.poolMode !== 'total'}
                {...formik.getFieldProps('poolgames')}
                error={formik.touched.poolgames && formik.errors.poolgames}
              />
            </div>
          </div>
        </Fieldset>
        <Fieldset label='Playoffs' className='playoffs-fieldset'>
          <div className='playoffs-mode'>
            <div className='playoffs-row'>
              <RadioButton
                label=''
                name='playoffMode'
                value='rounds'
                selectedValue={formik.values.playoffMode}
                onChange={(val) => formik.setFieldValue('playoffMode', val)}
              />
              <Select
                className='playoffs-select'
                options={playoffRoundOptions}
                value={formik.values.playoffRounds}
                onChange={(val) => {
                  formik.setFieldValue('playoffRounds', val);
                  const startRound = getStartRoundFromLabel(val);
                  const numRounds = startRound
                    ? roundIndex('F') - roundIndex(startRound) + 1
                    : 0;
                  const totalGames =
                    numRounds > 0 ? Math.pow(2, numRounds) - 1 : '';
                  formik.setFieldValue(
                    'playoffs',
                    totalGames === '' ? '' : String(totalGames),
                  );
                }}
                placeholder='By rounds'
                error={
                  formik.touched.playoffRounds && formik.errors.playoffRounds
                }
                disabled={formik.values.playoffMode !== 'rounds'}
              />
            </div>
            <div className='playoffs-row'>
              <RadioButton
                label=''
                name='playoffMode'
                value='total'
                selectedValue={formik.values.playoffMode}
                onChange={(val) => formik.setFieldValue('playoffMode', val)}
              />
              <Input
                className='playoffs-input'
                name='playoffs'
                placeholder='In total'
                disabled={formik.values.playoffMode !== 'total'}
                {...formik.getFieldProps('playoffs')}
                error={formik.touched.playoffs && formik.errors.playoffs}
              />
            </div>
          </div>
        </Fieldset>
      </div>
      <button className='gen-btn' type='submit'>
        Generate
      </button>
      <Spinner show={spinner} />
    </form>
  );
}
